import { useState, useEffect, useCallback } from 'react';
import opsApi, { getOpsToken, setOpsToken, clearOpsToken } from '@/services/opsApi';

type Tab = 'overview' | 'migrations' | 'users' | 'cache' | 'migration-assistant';

interface HealthData {
  status: string;
  timestamp: string;
  uptime: number;
  nodeVersion: string;
  memoryMb: number;
  services: Record<string, { status: string; message?: string }>;
}

interface StatsData {
  stats: {
    totalUsers: number;
    onlineUsers: number;
    totalChannels: number;
    totalMessages: number;
    recentUsers: number;
  };
}

interface Migration {
  name: string;
}

interface MigrationsData {
  pending: Migration[];
  executed: Migration[];
}

interface OpsUser {
  id: string;
  username: string;
  email: string | null;
  status: string;
  isAdmin: boolean;
  isOwner: boolean;
  createdAt: string;
}

interface EncryptionInfo {
  algorithm: string;
  keyPresent: boolean;
  keyLength: number;
  note: string;
}

export default function OpsPanel() {
  const [authenticated, setAuthenticated] = useState(false);
  const [secret, setSecret] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<Tab>('overview');

  // Per-tab data
  const [health, setHealth] = useState<HealthData | null>(null);
  const [stats, setStats] = useState<StatsData | null>(null);
  const [migrations, setMigrations] = useState<MigrationsData | null>(null);
  const [users, setUsers] = useState<OpsUser[]>([]);
  const [encInfo, setEncInfo] = useState<EncryptionInfo | null>(null);

  // Operation states
  const [loadingTab, setLoadingTab] = useState(false);
  const [opMessage, setOpMessage] = useState('');
  const [opError, setOpError] = useState('');
  const [exportedKey, setExportedKey] = useState('');
  const [confirmExport, setConfirmExport] = useState(false);
  const [flushConfirm, setFlushConfirm] = useState(false);
  const [migRunning, setMigRunning] = useState(false);
  const [deletingUserId, setDeletingUserId] = useState<string | null>(null);

  useEffect(() => {
    if (getOpsToken()) {
      setAuthenticated(true);
    }
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError('');
    setLoginLoading(true);
    try {
      const resp = await opsApi.post('/auth', { secret });
      setOpsToken(resp.data.token);
      setSecret('');
      setAuthenticated(true);
    } catch {
      setLoginError('Invalid secret or ops panel is not configured.');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = useCallback(() => {
    clearOpsToken();
    setAuthenticated(false);
    setHealth(null);
    setStats(null);
    setMigrations(null);
    setUsers([]);
    setEncInfo(null);
    setExportedKey('');
  }, []);

  const clearMessages = () => {
    setOpMessage('');
    setOpError('');
  };

  const loadOverview = useCallback(async () => {
    setLoadingTab(true);
    try {
      const [h, s] = await Promise.all([opsApi.get('/health'), opsApi.get('/stats')]);
      setHealth(h.data);
      setStats(s.data);
    } catch (e: any) {
      if (e.response?.status === 404) handleLogout();
    } finally {
      setLoadingTab(false);
    }
  }, [handleLogout]);

  const loadMigrations = useCallback(async () => {
    setLoadingTab(true);
    try {
      const resp = await opsApi.get('/migrations');
      setMigrations(resp.data);
    } catch (e: any) {
      if (e.response?.status === 404) handleLogout();
    } finally {
      setLoadingTab(false);
    }
  }, [handleLogout]);

  const loadUsers = useCallback(async () => {
    setLoadingTab(true);
    try {
      const resp = await opsApi.get('/users');
      setUsers(resp.data.users);
    } catch (e: any) {
      if (e.response?.status === 404) handleLogout();
    } finally {
      setLoadingTab(false);
    }
  }, [handleLogout]);

  const loadEncInfo = useCallback(async () => {
    setLoadingTab(true);
    try {
      const resp = await opsApi.get('/encryption/info');
      setEncInfo(resp.data);
    } catch (e: any) {
      if (e.response?.status === 404) handleLogout();
    } finally {
      setLoadingTab(false);
    }
  }, [handleLogout]);

  useEffect(() => {
    if (!authenticated) return;
    clearMessages();
    setExportedKey('');
    setConfirmExport(false);
    setFlushConfirm(false);
    if (activeTab === 'overview') loadOverview();
    if (activeTab === 'migrations') loadMigrations();
    if (activeTab === 'users') loadUsers();
    if (activeTab === 'migration-assistant') loadEncInfo();
  }, [authenticated, activeTab, loadOverview, loadMigrations, loadUsers, loadEncInfo]);

  const runMigrations = async () => {
    setMigRunning(true);
    clearMessages();
    try {
      const resp = await opsApi.post('/migrations/run');
      setOpMessage(resp.data.message);
      await loadMigrations();
    } catch (e: any) {
      setOpError(e.response?.data?.error || 'Migration failed');
    } finally {
      setMigRunning(false);
    }
  };

  const flushCache = async () => {
    clearMessages();
    try {
      const resp = await opsApi.post('/cache/flush');
      setOpMessage(resp.data.message);
      setFlushConfirm(false);
    } catch (e: any) {
      setOpError(e.response?.data?.error || 'Flush failed');
    }
  };

  const exportKey = async () => {
    clearMessages();
    try {
      const resp = await opsApi.post('/encryption/export', { confirm: 'EXPORT_ENCRYPTION_KEY' });
      setExportedKey(resp.data.encryptionKey);
      setConfirmExport(false);
    } catch (e: any) {
      setOpError(e.response?.data?.error || 'Export failed');
    }
  };

  const updateUserRole = async (userId: string, isAdmin: boolean, isOwner: boolean) => {
    clearMessages();
    try {
      await opsApi.put(`/users/${userId}/role`, { isAdmin, isOwner });
      setOpMessage('Role updated');
      await loadUsers();
    } catch (e: any) {
      setOpError(e.response?.data?.error || 'Update failed');
    }
  };

  const deleteUser = async (userId: string) => {
    setDeletingUserId(userId);
    clearMessages();
    try {
      await opsApi.delete(`/users/${userId}`);
      setOpMessage('User deleted');
      setUsers((prev) => prev.filter((u) => u.id !== userId));
    } catch (e: any) {
      setOpError(e.response?.data?.error || 'Delete failed');
    } finally {
      setDeletingUserId(null);
    }
  };

  if (!authenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-background">
        <div className="w-full max-w-sm">
          <div className="bg-brand-surface rounded-2xl shadow-2xl p-8">
            <div className="text-center mb-8">
              <div className="w-14 h-14 bg-brand-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-7 h-7 text-brand-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h1 className="text-2xl font-bold text-brand-on-background">Ops Panel</h1>
              <p className="text-sm text-brand-on-surface-variant mt-1">Enter your ops secret to continue</p>
            </div>
            <form onSubmit={handleLogin} className="space-y-4">
              <input
                type="password"
                placeholder="Ops secret"
                value={secret}
                onChange={(e) => setSecret(e.target.value)}
                className="w-full px-4 py-3 bg-brand-background rounded-xl border border-brand-surface-variant text-brand-on-background placeholder-brand-on-surface-variant focus:outline-none focus:ring-2 focus:ring-brand-primary/50"
                autoFocus
                required
              />
              {loginError && (
                <p className="text-sm text-brand-error">{loginError}</p>
              )}
              <button
                type="submit"
                disabled={loginLoading}
                className="w-full py-3 bg-brand-primary text-brand-on-primary rounded-xl font-semibold hover:opacity-90 transition disabled:opacity-50"
              >
                {loginLoading ? 'Authenticating…' : 'Enter'}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'migrations', label: 'Migrations' },
    { id: 'users', label: 'Users' },
    { id: 'cache', label: 'Cache' },
    { id: 'migration-assistant', label: 'Migration Assistant' },
  ];

  const statusDot = (status: string) => (
    <span className={`inline-block w-2 h-2 rounded-full mr-2 ${status === 'ok' ? 'bg-green-500' : 'bg-red-500'}`} />
  );

  return (
    <div className="min-h-screen bg-brand-background text-brand-on-background">
      {/* Header */}
      <header className="bg-brand-surface border-b border-brand-surface-variant px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <svg className="w-5 h-5 text-brand-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <h1 className="text-lg font-semibold">Ops Panel</h1>
        </div>
        <button
          onClick={handleLogout}
          className="text-sm text-brand-on-surface-variant hover:text-brand-on-background transition"
        >
          Sign out
        </button>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <nav className="w-52 min-h-[calc(100vh-64px)] bg-brand-surface border-r border-brand-surface-variant p-4 flex flex-col gap-1">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition ${
                activeTab === tab.id
                  ? 'bg-brand-primary text-brand-on-primary'
                  : 'text-brand-on-surface-variant hover:bg-brand-background hover:text-brand-on-background'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>

        {/* Main content */}
        <main className="flex-1 p-8">
          {(opMessage || opError) && (
            <div className={`mb-6 px-4 py-3 rounded-xl text-sm font-medium ${
              opError
                ? 'bg-red-500/10 text-red-400 border border-red-500/20'
                : 'bg-green-500/10 text-green-400 border border-green-500/20'
            }`}>
              {opError || opMessage}
              <button onClick={clearMessages} className="ml-3 opacity-60 hover:opacity-100">✕</button>
            </div>
          )}

          {loadingTab && (
            <div className="text-brand-on-surface-variant text-sm animate-pulse">Loading…</div>
          )}

          {/* OVERVIEW */}
          {activeTab === 'overview' && !loadingTab && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold">System Overview</h2>
              {health && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-brand-surface rounded-xl p-5 border border-brand-surface-variant">
                    <p className="text-xs uppercase tracking-wide text-brand-on-surface-variant mb-3">Services</p>
                    {Object.entries(health.services).map(([name, svc]) => (
                      <div key={name} className="flex items-center py-1">
                        {statusDot(svc.status)}
                        <span className="capitalize text-sm">{name}</span>
                        {svc.message && <span className="ml-2 text-xs text-brand-error">{svc.message}</span>}
                      </div>
                    ))}
                  </div>
                  <div className="bg-brand-surface rounded-xl p-5 border border-brand-surface-variant">
                    <p className="text-xs uppercase tracking-wide text-brand-on-surface-variant mb-3">Runtime</p>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between"><span className="text-brand-on-surface-variant">Status</span><span className={health.status === 'ok' ? 'text-green-400' : 'text-red-400'}>{health.status}</span></div>
                      <div className="flex justify-between"><span className="text-brand-on-surface-variant">Uptime</span><span>{Math.round(health.uptime / 60)}m</span></div>
                      <div className="flex justify-between"><span className="text-brand-on-surface-variant">Memory</span><span>{health.memoryMb} MB</span></div>
                      <div className="flex justify-between"><span className="text-brand-on-surface-variant">Node</span><span>{health.nodeVersion}</span></div>
                    </div>
                  </div>
                </div>
              )}
              {stats && (
                <div className="grid grid-cols-5 gap-3">
                  {[
                    { label: 'Total Users', value: stats.stats.totalUsers },
                    { label: 'Online', value: stats.stats.onlineUsers },
                    { label: 'Channels', value: stats.stats.totalChannels },
                    { label: 'Messages', value: stats.stats.totalMessages },
                    { label: 'New (7d)', value: stats.stats.recentUsers },
                  ].map((item) => (
                    <div key={item.label} className="bg-brand-surface rounded-xl p-4 border border-brand-surface-variant text-center">
                      <p className="text-2xl font-bold text-brand-primary">{item.value.toLocaleString()}</p>
                      <p className="text-xs text-brand-on-surface-variant mt-1">{item.label}</p>
                    </div>
                  ))}
                </div>
              )}
              <button
                onClick={loadOverview}
                className="text-sm text-brand-primary hover:underline"
              >
                Refresh
              </button>
            </div>
          )}

          {/* MIGRATIONS */}
          {activeTab === 'migrations' && !loadingTab && migrations && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold">Database Migrations</h2>
                <button
                  onClick={runMigrations}
                  disabled={migRunning || migrations.pending.length === 0}
                  className="px-4 py-2 bg-brand-primary text-brand-on-primary rounded-lg text-sm font-medium disabled:opacity-40 hover:opacity-90 transition"
                >
                  {migRunning ? 'Running…' : `Run ${migrations.pending.length} pending`}
                </button>
              </div>

              {migrations.pending.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-brand-on-surface-variant mb-2">Pending</p>
                  <div className="bg-brand-surface rounded-xl border border-brand-surface-variant overflow-hidden">
                    {migrations.pending.map((m) => (
                      <div key={m.name} className="px-4 py-3 border-b border-brand-surface-variant last:border-0 flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-yellow-400 flex-shrink-0" />
                        <span className="text-sm font-mono">{m.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <p className="text-sm font-medium text-brand-on-surface-variant mb-2">Applied ({migrations.executed.length})</p>
                <div className="bg-brand-surface rounded-xl border border-brand-surface-variant overflow-hidden">
                  {migrations.executed.length === 0 && (
                    <p className="px-4 py-3 text-sm text-brand-on-surface-variant">None applied yet</p>
                  )}
                  {migrations.executed.map((m) => (
                    <div key={m.name} className="px-4 py-3 border-b border-brand-surface-variant last:border-0 flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0" />
                      <span className="text-sm font-mono text-brand-on-surface-variant">{m.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* USERS */}
          {activeTab === 'users' && !loadingTab && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold">Users ({users.length})</h2>
                <button onClick={loadUsers} className="text-sm text-brand-primary hover:underline">Refresh</button>
              </div>
              <div className="bg-brand-surface rounded-xl border border-brand-surface-variant overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-brand-surface-variant">
                      <th className="text-left px-4 py-3 text-brand-on-surface-variant font-medium">User</th>
                      <th className="text-left px-4 py-3 text-brand-on-surface-variant font-medium">Status</th>
                      <th className="text-left px-4 py-3 text-brand-on-surface-variant font-medium">Roles</th>
                      <th className="text-left px-4 py-3 text-brand-on-surface-variant font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user.id} className="border-b border-brand-surface-variant last:border-0">
                        <td className="px-4 py-3">
                          <p className="font-medium">{user.username}</p>
                          <p className="text-xs text-brand-on-surface-variant">{user.email || '—'}</p>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full ${
                            user.status !== 'offline' ? 'bg-green-500/10 text-green-400' : 'bg-brand-background text-brand-on-surface-variant'
                          }`}>
                            {user.status}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-1 flex-wrap">
                            {user.isOwner && <span className="text-xs px-2 py-0.5 bg-brand-primary/10 text-brand-primary rounded-full">Owner</span>}
                            {user.isAdmin && !user.isOwner && <span className="text-xs px-2 py-0.5 bg-blue-500/10 text-blue-400 rounded-full">Admin</span>}
                            {!user.isAdmin && !user.isOwner && <span className="text-xs text-brand-on-surface-variant">User</span>}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2">
                            {!user.isOwner && (
                              <button
                                onClick={() => updateUserRole(user.id, !user.isAdmin, user.isOwner)}
                                className="text-xs px-2 py-1 bg-blue-500/10 text-blue-400 rounded hover:bg-blue-500/20 transition"
                              >
                                {user.isAdmin ? 'Revoke admin' : 'Make admin'}
                              </button>
                            )}
                            <button
                              onClick={() => updateUserRole(user.id, user.isAdmin, !user.isOwner)}
                              className="text-xs px-2 py-1 bg-brand-primary/10 text-brand-primary rounded hover:bg-brand-primary/20 transition"
                            >
                              {user.isOwner ? 'Revoke owner' : 'Make owner'}
                            </button>
                            <button
                              onClick={() => deleteUser(user.id)}
                              disabled={deletingUserId === user.id}
                              className="text-xs px-2 py-1 bg-red-500/10 text-red-400 rounded hover:bg-red-500/20 transition disabled:opacity-40"
                            >
                              {deletingUserId === user.id ? '…' : 'Delete'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* CACHE */}
          {activeTab === 'cache' && (
            <div className="space-y-6 max-w-lg">
              <h2 className="text-xl font-semibold">Cache Management</h2>
              <div className="bg-brand-surface rounded-xl border border-brand-surface-variant p-6">
                <h3 className="font-medium mb-2">Flush Redis Cache</h3>
                <p className="text-sm text-brand-on-surface-variant mb-4">
                  Clears all cached data. Active sessions will need to re-fetch data. Use after config changes or to resolve stale data issues.
                </p>
                {!flushConfirm ? (
                  <button
                    onClick={() => setFlushConfirm(true)}
                    className="px-4 py-2 bg-red-500/10 text-red-400 rounded-lg text-sm font-medium hover:bg-red-500/20 transition"
                  >
                    Flush all cache
                  </button>
                ) : (
                  <div className="flex gap-3">
                    <button
                      onClick={flushCache}
                      className="px-4 py-2 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600 transition"
                    >
                      Confirm flush
                    </button>
                    <button
                      onClick={() => setFlushConfirm(false)}
                      className="px-4 py-2 bg-brand-background text-brand-on-background rounded-lg text-sm font-medium hover:opacity-80 transition"
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* MIGRATION ASSISTANT */}
          {activeTab === 'migration-assistant' && !loadingTab && (
            <div className="space-y-6 max-w-2xl">
              <h2 className="text-xl font-semibold">Migration Assistant</h2>
              <p className="text-sm text-brand-on-surface-variant">
                Use this panel when migrating your IRUS installation to a new server. Export your encryption key and set it as <code className="bg-brand-background px-1.5 py-0.5 rounded text-brand-primary">ENCRYPTION_KEY</code> in the new deployment so encrypted files remain accessible.
              </p>

              {encInfo && (
                <div className="bg-brand-surface rounded-xl border border-brand-surface-variant p-6 space-y-4">
                  <h3 className="font-medium">Encryption Key</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-brand-on-surface-variant">Algorithm</span>
                      <span>{encInfo.algorithm}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-brand-on-surface-variant">Key present</span>
                      <span className={encInfo.keyPresent ? 'text-green-400' : 'text-red-400'}>
                        {encInfo.keyPresent ? 'Yes' : 'No'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-brand-on-surface-variant">Key length</span>
                      <span>{encInfo.keyLength} chars</span>
                    </div>
                  </div>

                  {exportedKey ? (
                    <div className="space-y-3">
                      <div className="bg-brand-background rounded-lg p-4 border border-red-500/30">
                        <p className="text-xs text-brand-on-surface-variant mb-2 font-medium uppercase tracking-wide">ENCRYPTION_KEY — keep secret</p>
                        <code className="text-sm break-all text-brand-primary font-mono select-all">{exportedKey}</code>
                      </div>
                      <p className="text-xs text-brand-on-surface-variant">
                        Set this as <code className="bg-brand-background px-1 rounded">ENCRYPTION_KEY</code> in your new deployment's secrets before importing data.
                        This key is only shown once per session.
                      </p>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(exportedKey);
                          setOpMessage('Key copied to clipboard');
                        }}
                        className="text-sm px-3 py-1.5 bg-brand-primary/10 text-brand-primary rounded-lg hover:bg-brand-primary/20 transition"
                      >
                        Copy to clipboard
                      </button>
                    </div>
                  ) : !confirmExport ? (
                    <button
                      onClick={() => setConfirmExport(true)}
                      className="px-4 py-2 bg-brand-primary/10 text-brand-primary rounded-lg text-sm font-medium hover:bg-brand-primary/20 transition"
                    >
                      Export encryption key
                    </button>
                  ) : (
                    <div className="space-y-3">
                      <p className="text-sm text-brand-on-surface-variant bg-yellow-500/10 border border-yellow-500/20 rounded-lg px-4 py-3">
                        You are about to export the raw encryption key. Store it immediately in a secure location.
                      </p>
                      <div className="flex gap-3">
                        <button
                          onClick={exportKey}
                          className="px-4 py-2 bg-brand-primary text-brand-on-primary rounded-lg text-sm font-medium hover:opacity-90 transition"
                        >
                          Yes, export key
                        </button>
                        <button
                          onClick={() => setConfirmExport(false)}
                          className="px-4 py-2 bg-brand-background text-brand-on-background rounded-lg text-sm font-medium hover:opacity-80 transition"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="bg-brand-surface rounded-xl border border-brand-surface-variant p-6 space-y-3">
                <h3 className="font-medium">Server Migration Checklist</h3>
                <ol className="space-y-2 text-sm text-brand-on-surface-variant list-decimal list-inside">
                  <li>Export the encryption key above and save it securely</li>
                  <li>Back up your PostgreSQL database</li>
                  <li>Back up your S3/MinIO bucket contents</li>
                  <li>Set <code className="bg-brand-background px-1 rounded text-brand-primary">ENCRYPTION_KEY</code> in the new server's secrets</li>
                  <li>Restore database backup on the new server</li>
                  <li>Restore object storage files on the new server</li>
                  <li>Run <code className="bg-brand-background px-1 rounded text-brand-primary">npm run db:migrate</code> on the new server</li>
                  <li>Verify encrypted files are accessible after cutover</li>
                </ol>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
